import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";

const line1 = ["Build", "a", "specialized"];
const line2 = ["capability", "center"];
const line3 = ["in", "90—120", "days."];

const wordVar = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 0.9,
      ease: [0.2, 0.7, 0.2, 1],
      delay: 0.35 + i * 0.06,
    },
  }),
};

function MaskedLine({ words, offset = 0, italic = false }) {
  return (
    <span className="block whitespace-normal">
      {words.map((w, i) => (
        <span
          key={i}
          className="reveal-mask align-baseline mr-3 md:mr-5 last:mr-0"
        >
          <motion.span
            custom={i + offset}
            variants={wordVar}
            initial="hidden"
            animate="show"
            className={`inline-block ${italic ? "italic text-gold" : ""}`}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] hero-vignette overflow-hidden pt-24 md:pt-32 pb-16"
      data-testid="hero-section"
    >
      <div className="grain absolute inset-0" />

      {/* Editorial meta rail */}
      <div className="absolute left-6 md:left-10 top-24 md:top-32 z-10 flex flex-col gap-3 text-[10px] tracking-eyebrow uppercase text-ivory-dim">
        <div className="flex items-center gap-2">
          <span className="h-px w-6 bg-gold" />
          <span>Vol. 01 — 2026</span>
        </div>
        <span>Capability Center as a Service</span>
      </div>

      <div className="absolute right-6 md:right-10 top-24 md:top-32 z-10 flex flex-col items-end gap-2 text-[10px] tracking-eyebrow uppercase text-ivory-dim">
        <span>UAE · Singapore · India</span>
        <span className="text-emerald">● 3 hubs · 6 frameworks</span>
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 pt-16 md:pt-20">
        <motion.h1
          style={{ y: y2 }}
          className="font-display font-light tracking-tight text-ivory text-[13vw] md:text-[9vw] lg:text-[8.2vw] leading-[0.95]"
        >
          <MaskedLine words={line1} offset={0} />
          <MaskedLine words={line2} offset={line1.length} italic />
          <MaskedLine words={line3} offset={line1.length + line2.length} />
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <p className="md:col-span-6 text-ivory/80 text-base md:text-lg leading-relaxed max-w-xl">
            For family offices, private offices, and conglomerates rewriting the
            playbook from{" "}
            <em className="text-gold not-italic font-medium">passive preservation</em>{" "}
            to <em className="text-emerald not-italic font-medium">active value creation</em>.
            We architect, staff, and operate specialized capability centers as
            an extension of your principal&rsquo;s office.
          </p>

          <div className="md:col-span-6 flex flex-col md:items-end gap-4">
            <div className="flex flex-wrap gap-3">
              <a
                href="#workshop"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 px-6 py-4 bg-gold text-ink font-medium tracking-wide hover:bg-gold-soft transition-colors"
              >
                Schedule a Strategy Workshop
                <ArrowUpRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>
              <a
                href="#approach"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-4 border border-[var(--line-strong)] text-ivory hover:border-gold hover:text-gold transition-colors"
              >
                Read the Manifesto
              </a>
            </div>
            <p className="text-xs tracking-eyebrow uppercase text-ivory-dim">
              40—60% lower cost · Modular · ROI visible
            </p>
          </div>
        </motion.div>
      </div>

      {/* Hero visual — clipped, spotlight-treated */}
      <motion.div
        style={{ y: y1, opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1.4 }}
        className="relative z-0 mt-12 md:mt-20 mx-auto max-w-[1400px] px-6 md:px-10"
      >
        <div className="relative overflow-hidden border border-[var(--line)] spotlight">
          <img
            src="https://images.pexels.com/photos/12696432/pexels-photo-12696432.jpeg"
            alt="Abstract architectural structure"
            className="w-full h-[38vh] md:h-[52vh] object-cover"
            loading="eager"
          />
          <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 flex items-center gap-3 text-[10px] tracking-eyebrow uppercase text-ivory-dim z-10">
            <span className="h-px w-6 bg-gold" />
            <span>Fig. 01 · Structural clarity</span>
          </div>
          <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center gap-2 text-[10px] tracking-eyebrow uppercase text-ivory z-10">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald animate-pulse" />
            <span>Live mandate · Dubai</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
